import React, { FunctionComponent, useEffect, useState } from 'react';
import { countryCodeSelectStyles } from './PhoneInput.countryCodeSelectStyles';
import cx from 'classnames';
import {
  AsYouType,
  CountryCode,
  formatIncompletePhoneNumber,
} from 'libphonenumber-js';

import BaseInput, { InputProps } from '../BaseInput';
import styles from './PhoneInput.module.scss';
import inputStyles from '../BaseInput/BaseInput.module.scss';
import { SUPPORTED_COUNTRIES, Country } from '../constants';
import Select from '../Select';
import { useUniqueId } from '../utils';
import { createFilter } from 'react-select';

export interface Props
  extends Pick<InputProps, Exclude<keyof InputProps, 'label' | 'onChange'>> {
  /** Can be used to manually force OS-native select menu instead of react-select control (for testing). By default, only mobile browsers will use OS-native version. */
  renderNativeSelect?: boolean;
  /** A list of `Country`s to populate the country/calling code select list */
  countryCodeList?: Country[];
  /** Custom label for the calling code field */
  callingCodeLabel?: string;
  /** Custom label for the phone number field */
  phoneNumberLabel?: string;
  onChange: (newVal: string) => void;
}

export const PhoneInput: FunctionComponent<Props> = ({
  callingCodeLabel = 'Country code',
  countryCodeList = SUPPORTED_COUNTRIES,
  disabled,
  id,
  isDarkMode,
  onChange,
  phoneNumberLabel = 'Phone number',
  value,
  renderNativeSelect = /Android|webOS|iPhone|iPad|iPod|Opera Mini/i.test(
    navigator.userAgent,
  ),
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const uniqueCallingCodeId = useUniqueId('calling-code-input');
  const uniquePhoneInputId = useUniqueId('phone-input');

  const callingCode =
    value == null || value.length === 0
      ? // if input is empty / untouched, default to first calling code
        countryCodeList[0].callingCode
      : // else get country code from the (possibly incomplete) number
        formatIncompletePhoneNumber(value).split(' ')[0];

  const [phoneNumber, setPhoneNumber] = React.useState(
    value?.replace(callingCode, '') || '',
  );
  useEffect(() => {
    setPhoneNumber(value?.replace(callingCode, '') || '');
  }, [value, callingCode]);

  // map country codes to label/value options for select
  const selectOptions = countryCodeList.map((c: Country) => ({
    ...c,
    label: `${c.icon} ${c.callingCode}`,
    longLabel: `${c.name} ${c.callingCode}`,
    value: c.callingCode,
  }));

  const selectValue =
    selectOptions.find(o => o.callingCode === callingCode) || selectOptions[0];

  // formatted phone number for display when input is not focused
  const formattedPhoneNumber = new AsYouType(
    (selectValue.code as CountryCode) || 'US',
  ).input(phoneNumber);

  const handleCallingCodeChange = (option: { value: string }): void => {
    onChange(`${option.value}${phoneNumber.length > 0 ? phoneNumber : ''}`);
  };

  return (
    <BaseInput
      {...props}
      id={id || uniquePhoneInputId}
      value={isFocused ? phoneNumber : formattedPhoneNumber}
      isDarkMode={isDarkMode}
      disabled={disabled}
      autoComplete='tel'
      inputMode='tel'
      type='tel'
      onBlur={() => {
        setIsFocused(false);
      }}
      onFocus={() => {
        setIsFocused(true);
      }}
      onChange={({ target }) => {
        // Remove all non-numeric characters in the phone number value
        const sanitizedValue = target.value.replace(/[^0-9]+/g, '');
        onChange(`${callingCode}${sanitizedValue}`);
        setPhoneNumber(sanitizedValue);
      }}
      onKeyPress={e => {
        // Accept only numbers as input
        if (/[^0-9]/.test(e.key)) {
          e.preventDefault();
        }
      }}
      label={phoneNumberLabel}
      className={cx(
        styles.phoneInput,
        !renderNativeSelect && styles.selectReplacementPhoneInput,
      )}
      inputClassName={styles.input}
      labelClassName={styles.label}
      prefixClassName={cx(
        styles.prefix,
        !renderNativeSelect && styles.selectReplacementPrefix,
      )}
      prefix={({ onFocus, onBlur }) =>
        renderNativeSelect ? (
          <>
            <label
              htmlFor={uniqueCallingCodeId}
              className={cx(
                inputStyles.floatLabel,
                isDarkMode && inputStyles.floatLabelDark,
                styles.selectLabel,
              )}
            >
              {callingCodeLabel}
            </label>
            <select
              className={cx(
                inputStyles.input,
                inputStyles.select,
                inputStyles['prefix-input'],
                styles.select,
                isDarkMode && styles.selectDark,
              )}
              disabled={disabled}
              id={uniqueCallingCodeId}
              onChange={({ target }) => {
                handleCallingCodeChange(
                  selectOptions.find(c => c.callingCode === target.value)!,
                );
              }}
              value={selectValue.value}
            >
              {selectOptions.map(c => (
                <option
                  key={c.code}
                  value={c.callingCode}
                  className={styles.phoneOption}
                  aria-label={`${c.name} +${c.callingCode}`}
                >
                  {`${callingCode === c.callingCode ? c.icon : c.name} ${
                    c.callingCode
                  }`}
                </option>
              ))}
            </select>
          </>
        ) : (
          <Select
            className={styles.countryCodeSelect}
            customStyles={countryCodeSelectStyles}
            defaultValue={selectValue}
            disabled={disabled}
            filterOption={createFilter({
              matchFrom: 'any',
              stringify: (option: any) =>
                `${option.label} ${option.value} ${option.data.name}`,
            })}
            id={uniqueCallingCodeId}
            isDarkMode={isDarkMode}
            label={callingCodeLabel}
            labelClassName={styles.selectReplacementLabel}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={v => {
              handleCallingCodeChange(v);
            }}
            value={selectValue}
            options={selectOptions}
            formatOptionLabel={({ longLabel, label }, { context }) => {
              // If the option is being shown in the dropdown list, render the long version of its value,
              // otherwise render the short version
              return context === 'menu' ? longLabel : label;
            }}
          />
        )
      }
    />
  );
};

PhoneInput.displayName = 'PhoneInput';
export default PhoneInput;
